// # Financial System Component for Admin
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../auth/state';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { auth, db } from '../../../auth/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import cacheManager from '../../../auth/cache_manager';
import salaryIcon from '../../../aneo_media/finance/salary.png';
import expensesIcon from '../../../aneo_media/finance/expenses.png';
import revenueIcon from '../../../aneo_media/finance/revenue.png';
import paymentIcon from '../../../aneo_media/finance/payment.png';
import bankIcon from '../../../aneo_media/finance/bank.png';
import TransactionIcon from '../../../aneo_media/finance/transaction.png';
import './financial_system.css';

const FinancialSystem = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuthStore();
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Redirect if not admin
  React.useEffect(() => {
    const startTime = performance.now();
    
    console.log('Financial System - Admin access check:', {
      operation: 'admin_check',
      isAdmin: !!isAdmin,
      timestamp: new Date().toISOString(),
      timeElapsed: `${(performance.now() - startTime).toFixed(2)}ms`
    });

    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  // # Financial system sections
  const financialSections = [
    {
      title: 'The Bank',
      icon: bankIcon,
      path: '/admin/financial/bank',
      description: 'Manage team member banking information'
    },
    {
      title: 'Payment Processing',
      icon: paymentIcon,
      path: '/admin/financial/payments',
      description: 'Handle payment processing and transactions'
    },
    {
      title: 'Expenses Tracking',
      icon: expensesIcon,
      path: '/admin/financial/expenses',
      description: 'Track and manage business expenses'
    },
    {
      title: 'Net Revenue Analytics',
      icon: revenueIcon,
      path: '/admin/financial/revenue',
      description: 'Monitor and analyze net revenue streams'
    },
    {
      title: 'Salary Management',
      icon: salaryIcon,
      path: '/admin/financial/salary',
      description: 'Manage employee and contractor salaries'
    },
    {
      title: 'Transaction Logs',
      icon: TransactionIcon,
      path: '/admin/financial/transactions',
      description: 'View and manage transaction logs'
    }
  ];

  return (
    <div className={`fnc_sys_admin-page ${isDarkMode ? 'fnc_sys_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <main className="fnc_sys_admin-content">
        <div className="fnc_sys_admin-header">
          <h1>Financial System</h1>
        </div>

        <div className="fnc_sys_financial-grid">
          {/* # First row with three cards */}
          <div className="fnc_sys_grid-row">
            {financialSections.slice(0, 3).map((section, index) => (
              <div 
                key={index}
                className="fnc_sys_financial-card"
                onClick={() => navigate(section.path)}
              >
                <div className="fnc_sys_card-icon">
                  <img src={section.icon} alt={section.title} />
                </div>
                <h3>{section.title}</h3>
                <p>{section.description}</p>
              </div>
            ))}
          </div>
          
          {/* # Second row with three cards */}
          <div className="fnc_sys_grid-row">
            {financialSections.slice(3, 6).map((section, index) => (
              <div 
                key={index + 3}
                className="fnc_sys_financial-card"
                onClick={() => navigate(section.path)}
              >
                <div className="fnc_sys_card-icon">
                  <img src={section.icon} alt={section.title} />
                </div>
                <h3>{section.title}</h3>
                <p>{section.description}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
};

export default FinancialSystem; 